import React, { useState, useRef, useEffect } from 'react';

const VideoPlayer = ({ isDarkMode }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef(null);

  // Lista dos vídeos disponíveis
  const videos = [
    {
      id: 'angustia',
      title: 'Lidando com a Angústia',
      description: 'Técnicas práticas para superar momentos de angústia e ansiedade extrema',
      filename: 'angustia_01.mp4',
      duration: '5:30',
      category: 'Bem-estar Mental'
    },
    {
      id: 'ansiedade',
      title: 'Controlando a Ansiedade',
      description: 'Estratégias eficazes para gerenciar a ansiedade no dia a dia',
      filename: 'ansiedade_01.mp4',
      duration: '6:15',
      category: 'Controle Emocional'
    },
    {
      id: 'tristeza',
      title: 'Superando a Tristeza',
      description: 'Como transformar momentos de tristeza em oportunidades de crescimento',
      filename: 'tristeza_01.mp4',
      duration: '4:45',
      category: 'Desenvolvimento Pessoal'
    }
  ];

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
    setIsPlaying(false);
    setIsLoading(true);
    setCurrentTime(0);
    setDuration(0);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
    setIsLoading(false);
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    if (videoRef.current && duration > 0) {
      const rect = e.target.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  return (
    <div style={{
      margin: '3rem 0',
      padding: '2rem',
      background: isDarkMode ? '#1f2937' : '#f9fafb',
      borderRadius: '12px'
    }}>
      <h2 style={{
        fontSize: '2rem',
        marginBottom: '2rem',
        textAlign: 'center',
        color: isDarkMode ? '#a5b4fc' : '#4f46e5'
      }}>
        📹 Vídeos Motivacionais
      </h2>

      <p style={{
        textAlign: 'center',
        marginBottom: '2rem',
        fontSize: '1.1rem',
        color: isDarkMode ? '#9ca3af' : '#6b7280'
      }}>
        Conteúdo exclusivo para ajudar você a superar desafios emocionais
      </p>

      {/* Lista de vídeos */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {videos.map((video) => (
          <div
            key={video.id}
            onClick={() => handleVideoSelect(video)}
            style={{
              padding: '1.5rem',
              background: isDarkMode ? '#374151' : 'white',
              borderRadius: '8px',
              cursor: 'pointer',
              border: selectedVideo?.id === video.id 
                ? `2px solid ${isDarkMode ? '#4f46e5' : '#4338ca'}` 
                : '2px solid transparent',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h3 style={{
              fontSize: '1.25rem',
              marginBottom: '0.5rem',
              color: isDarkMode ? '#e5e7eb' : '#1f2937'
            }}>
              {video.title}
            </h3>
            <p style={{
              fontSize: '0.85rem',
              color: isDarkMode ? '#a5b4fc' : '#4f46e5',
              marginBottom: '0.5rem',
              fontWeight: 'bold'
            }}>
              {video.category} • {video.duration}
            </p>
            <p style={{
              fontSize: '0.9rem',
              color: isDarkMode ? '#9ca3af' : '#6b7280',
              marginBottom: '1rem',
              lineHeight: '1.4'
            }}>
              {video.description}
            </p>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: isDarkMode ? '#a5b4fc' : '#4f46e5',
              fontSize: '0.9rem'
            }}>
              <span>▶️</span>
              <span>Clique para reproduzir</span>
            </div>
          </div>
        ))}
      </div>

      {/* Player de vídeo */}
      {selectedVideo && (
        <div style={{
          background: isDarkMode ? '#111827' : '#ffffff',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            marginBottom: '1rem',
            textAlign: 'center',
            color: isDarkMode ? '#e5e7eb' : '#1f2937'
          }}>
            {selectedVideo.title}
          </h3>
          
          <div style={{
            position: 'relative',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px'
              }}
              controls
              onEnded={handleVideoEnd}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              preload="metadata"
            >
              <source 
                src={`/video/${selectedVideo.filename}`} 
                type="video/mp4" 
              />
              Seu navegador não suporta o elemento de vídeo.
            </video>

            {/* Controles customizados adicionais */}
            <div style={{
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              {/* Barra de progresso */}
              <div style={{
                flex: '1',
                height: '6px',
                backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
                borderRadius: '3px',
                cursor: 'pointer',
                position: 'relative'
              }} onClick={handleSeek}>
                <div style={{
                  width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
                  height: '100%',
                  backgroundColor: isDarkMode ? '#4f46e5' : '#4338ca',
                  borderRadius: '3px',
                  transition: 'width 0.1s ease'
                }}></div>
              </div>
              
              {/* Tempo */}
              <div style={{
                fontSize: '0.85rem',
                color: isDarkMode ? '#9ca3af' : '#6b7280',
                minWidth: '80px'
              }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '1rem',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
              borderRadius: '6px',
              fontSize: '0.9rem'
            }}>
              <span style={{ color: isDarkMode ? '#a5b4fc' : '#4f46e5' }}>📂</span>
              <span style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                {selectedVideo.category}
              </span>
            </div>
            <p style={{
              color: isDarkMode ? '#9ca3af' : '#6b7280',
              fontSize: '0.9rem',
              marginTop: '0.75rem'
            }}>
              {selectedVideo.description}
            </p>
          </div>
        </div>
      )}

      {/* Instruções se nenhum vídeo estiver selecionado */}
      {!selectedVideo && (
        <div style={{
          textAlign: 'center',
          padding: '3rem 1rem',
          color: isDarkMode ? '#9ca3af' : '#6b7280'
        }}>
          <p style={{ fontSize: '1.1rem' }}>
            Selecione um vídeo acima para começar a assistir
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
